import { tooltipFactory } from '@milkdown/plugin-tooltip';
import type { EditorView } from '@milkdown/kit/prose/view';
import { usePluginViewContext } from '@prosemirror-adapter/react';
import { useLayoutEffect, useRef } from 'react';

import { useEditorAction } from '../../hooks/useEditorAction';
import { getEmojiSuggestState, type EmojiSuggestState } from './suggest';

export const EMOJI_TOOLTIP_NAME = 'emoji-suggest';

/** Milkdown tooltip slot for the `:` emoji list. */
export const emojiTooltip = tooltipFactory('BRAINFISH_EMOJI');

type ActivePanel = Extract<EmojiSuggestState, { active: true }>;

const MAX_H = 224;
const MAX_W = 288;
const GAP = 4;
const ROW_PX = 40;

function tooltipPosition(view: EditorView, panel: ActivePanel): { top: number; left: number; above: boolean } | null {
  const parent = view.dom.parentElement;
  if (!parent) return null;

  const coords = view.coordsAtPos(panel.from);
  const r = parent.getBoundingClientRect();
  const estHeight = Math.min(MAX_H, Math.max(80, panel.items.length * ROW_PX));
  const need = estHeight + GAP;

  const spaceBelow = r.bottom - coords.bottom;
  const spaceAbove = coords.top - r.top;
  const above = spaceBelow < need && (spaceAbove > spaceBelow || spaceAbove >= need);

  const rawLeft = coords.left - r.left;
  const left = Math.min(Math.max(GAP, rawLeft), Math.max(0, r.width - MAX_W - GAP));
  const top = above ? coords.top - r.top - GAP : coords.bottom - r.top + GAP;

  return { top, left, above };
}

export const EmojiTooltipView = () => {
  const { view } = usePluginViewContext();
  const { getInstance } = useEditorAction();
  const ctx = getInstance()?.ctx;
  const listRef = useRef<HTMLDivElement>(null);

  const panel = view?.editable ? getEmojiSuggestState(view.state) : ({ active: false } as const);
  const pos = view && panel.active ? tooltipPosition(view, panel) : null;

  useLayoutEffect(() => {
    if (!panel.active || !listRef.current) return;
    listRef.current.querySelector<HTMLElement>('.selected')?.scrollIntoView({ block: 'nearest' });
  }, [panel.active, panel.active ? panel.selectedIndex : 0]);

  if (!panel.active || !pos || !ctx) {
    return null;
  }

  return (
    <div
      ref={listRef}
      data-name={EMOJI_TOOLTIP_NAME}
      className="milkdown-slash-menu"
      style={{
        top: pos.top,
        left: pos.left,
        transform: pos.above ? 'translateY(-100%)' : undefined,
      }}
    >
      <div className="menu-groups">
        <div className="menu-group">
          <ul>
            {panel.items.map((item, index) => {
              const selected = index === panel.selectedIndex;

              return (
                <li key={`${item.name}-${index}`} data-index={index} className={selected ? 'hover' : undefined}>
                  <span className="text-lg leading-none" aria-hidden>
                    {item.emoji}
                  </span>
                  <span className="truncate opacity-80">:{item.name}:</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};
